import { supabase } from "@/integrations/supabase/client";

interface LogPayload {
  message: string;
  stack?: string;
  componentStack?: string;
  severity?: "info" | "warn" | "error" | "fatal";
  metadata?: Record<string, unknown>;
}

let lastSent: { key: string; at: number } | null = null;
const DEDUP_WINDOW_MS = 30_000;

// PERF: circuit-breaker global. Um erro em loop (ex.: dentro de um render)
// gerava centenas de INSERTs + round-trips de auth por minuto, degradando
// rede, bateria e o próprio banco. Máx. 10 relatórios por minuto por aba.
const RATE_WINDOW_MS = 60_000;
const RATE_MAX = 10;
let windowStart = 0;
let windowCount = 0;

/**
 * Reporta um erro de cliente para a tabela client_error_logs.
 * - Faz dedup simples por (mensagem+rota) para evitar flood.
 * - Aceita usuário anônimo (user_id = null).
 * - Nunca lança: falhas no envio são silenciosamente ignoradas (com console).
 */
export async function logClientError(payload: LogPayload): Promise<void> {
  try {
    if (typeof window === "undefined") return;

    const route = window.location?.pathname ?? null;
    const url = window.location?.href ?? null;
    const userAgent = navigator?.userAgent ?? null;

    const key = `${payload.message}|${route}`;
    const now = Date.now();
    if (lastSent && lastSent.key === key && now - lastSent.at < DEDUP_WINDOW_MS) {
      return;
    }
    lastSent = { key, at: now };

    if (now - windowStart > RATE_WINDOW_MS) {
      windowStart = now;
      windowCount = 0;
    }
    if (windowCount >= RATE_MAX) return;
    windowCount += 1;

    // getSession() lê do storage local; getUser() fazia round-trip de rede
    // a cada erro reportado.
    const { data: sessionRes } = await supabase.auth.getSession();
    const userId = sessionRes?.session?.user?.id ?? null;


    await supabase.from("client_error_logs").insert({
      user_id: userId,
      route,
      url,
      user_agent: userAgent,
      message: payload.message.slice(0, 2000),
      stack: payload.stack?.slice(0, 8000) ?? null,
      component_stack: payload.componentStack?.slice(0, 8000) ?? null,
      severity: payload.severity ?? "error",
      metadata: payload.metadata ?? null,
    } as never);
  } catch (e) {
    console.warn("[error-logger] failed to report", e);
  }
}

let installed = false;

/** Instala handlers globais (window.onerror + unhandledrejection) — uma única vez. */
export function installGlobalErrorHandlers(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (event) => {
    const err = event.error as Error | undefined;
    void logClientError({
      message: err?.message || event.message || "Unknown error",
      stack: err?.stack,
      severity: "error",
      metadata: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const message =
      reason instanceof Error
        ? reason.message
        : typeof reason === "string"
        ? reason
        : "Unhandled promise rejection";
    void logClientError({
      message,
      stack: reason instanceof Error ? reason.stack : undefined,
      severity: "error",
      metadata: { kind: "unhandledrejection" },
    });
  });
}
